import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, Shield, Search, Filter, ChevronDown, ChevronUp, Info, ArrowRight, RefreshCw } from 'lucide-react';
import RiskHeatmap from './RiskHeatmap';

// Updated component to fetch data from database API
// Replace hardcoded data with live database queries

const OccupationRiskTracker = () => {
  const [occupationData, setOccupationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [weekDate, setWeekDate] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRisk, setSelectedRisk] = useState('all');
  const [expandedCard, setExpandedCard] = useState(null);
  const [sortBy, setSortBy] = useState('risk');

  // Fetch data from API
  useEffect(() => {
    const fetchOccupationData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/app/api/occupation-risk.php');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setOccupationData(result.data.occupations);
          setWeekDate(result.data.weekDate);
          setLastUpdated(result.data.lastUpdated);
          setError(null);
        } else {
          throw new Error(result.error || 'Failed to load data');
        }
      } catch (err) {
        console.error('Error fetching occupation data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOccupationData();
  }, []);

  // Refresh data function
  const refreshData = async () => {
    const fetchOccupationData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/app/api/occupation-risk.php');
        const result = await response.json();
        
        if (result.success) {
          setOccupationData(result.data.occupations);
          setWeekDate(result.data.weekDate);
          setLastUpdated(result.data.lastUpdated);
          setError(null);
        }
      } catch (err) {
        console.error('Error refreshing data:', err);
      } finally {
        setLoading(false);
      }
    };

    await fetchOccupationData();
  };

  // Handle heatmap tile click - scroll to and expand card
  const handleHeatmapClick = (occupationId) => {
    setExpandedCard(occupationId);
    // Wait for state to update, then scroll to the card
    setTimeout(() => {
      const element = document.getElementById(`occupation-${occupationId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };
  
  // Get risk level helper
  const getRiskLevel = (score) => {
    if (score <= 25) return { level: 'Critical', color: 'bg-red-600', textColor: 'text-red-600' };
    if (score <= 40) return { level: 'High', color: 'bg-orange-500', textColor: 'text-orange-500' };
    if (score <= 60) return { level: 'Moderate', color: 'bg-yellow-500', textColor: 'text-yellow-500' };
    return { level: 'Lower', color: 'bg-green-500', textColor: 'text-green-500' };
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'declining':
      case 'collapsing':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'growing':
      case 'evolving':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'bifurcating':
        return <ArrowRight className="w-4 h-4 text-blue-500" />;
      default:
        return <Shield className="w-4 h-4 text-gray-500" />;
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = occupationData.filter(occ => {
      const matchesSearch = occ.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           occ.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || occ.category === selectedCategory;
      const matchesRisk = selectedRisk === 'all' || 
                         (selectedRisk === 'critical' && occ.riskScore <= 25) ||
                         (selectedRisk === 'high' && occ.riskScore > 25 && occ.riskScore <= 40) ||
                         (selectedRisk === 'moderate' && occ.riskScore > 40 && occ.riskScore <= 60) ||
                         (selectedRisk === 'lower' && occ.riskScore > 60);
      
      return matchesSearch && matchesCategory && matchesRisk;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'risk':
          return a.riskScore - b.riskScore;
        case 'employment':
          return b.employment - a.employment;
        case 'wage':
          return b.medianWage - a.medianWage;
        case 'name':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [occupationData, searchTerm, selectedCategory, selectedRisk, sortBy]);

  // Get unique categories
  const categories = useMemo(() => {
    return ['all', ...new Set(occupationData.map(occ => occ.category))];
  }, [occupationData]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading occupation risk data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Error Loading Data</h2>
          <p className="text-gray-600 mb-4 text-center">{error}</p>
          <button 
            onClick={refreshData}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Occupation Risk Tracker</h1>
              <p className="text-gray-600 mt-1">
                AI Automation Risk Assessment by SOC Major Group
              </p>
              {weekDate && (
                <p className="text-sm text-gray-500 mt-1">
                  Data for week of {new Date(weekDate).toLocaleDateString()}
                  {lastUpdated && ` • Last updated: ${new Date(lastUpdated).toLocaleString()}`}
                </p>
              )}
            </div>
            <button
              onClick={refreshData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-600"></div>
              <span>Critical Risk (0-25)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span>High Risk (26-40)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span>Moderate Risk (41-60)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Lower Risk (61-100)</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search occupations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </option>
              ))}
            </select>

            {/* Risk Filter */}
            <select
              value={selectedRisk}
              onChange={(e) => setSelectedRisk(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Risk Levels</option>
              <option value="critical">Critical Risk</option>
              <option value="high">High Risk</option>
              <option value="moderate">Moderate Risk</option>
              <option value="lower">Lower Risk</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="risk">Sort by Risk (Low to High)</option>
              <option value="employment">Sort by Employment</option>
              <option value="wage">Sort by Wage</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">Total Occupations</p>
            <p className="text-2xl font-bold text-gray-900">{occupationData.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">Critical/High Risk</p>
            <p className="text-2xl font-bold text-red-600">
              {occupationData.filter(o => o.riskScore <= 40).length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">Total Employment</p>
            <p className="text-2xl font-bold text-gray-900">
              {(occupationData.reduce((sum, o) => sum + o.employment, 0) / 1000000).toFixed(1)}M
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">Showing Results</p>
            <p className="text-2xl font-bold text-blue-600">{filteredAndSortedData.length}</p>
          </div>
        </div>

        {/* Risk Heatmap */}
        <div className="mb-6">
          <RiskHeatmap 
            occupations={occupationData} 
            onOccupationClick={handleHeatmapClick}
          />
        </div>

        {/* Occupation Cards */}
        {/* Occupation Cards */}
        <div className="space-y-4">
          {filteredAndSortedData.map(occupation => {
            const risk = getRiskLevel(occupation.riskScore);
            const isExpanded = expandedCard === occupation.id;

            return (
              <div 
                key={occupation.id} 
                id={`occupation-${occupation.id}`}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >

                {/* Card Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedCard(isExpanded ? null : occupation.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${risk.color}`}>
                          Risk Score: {occupation.riskScore}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-gray-600">
                          {getTrendIcon(occupation.trend)}
                          <span className="capitalize">{occupation.trend}</span>
                        </span>
                        {occupation.scoreChange !== 0 && (
                          <span className={`text-xs font-semibold ${occupation.scoreChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {occupation.scoreChange > 0 ? '↑' : '↓'} {Math.abs(occupation.scoreChange)} this week
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {occupation.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        SOC {occupation.socCode} • {occupation.category}
                      </p>
                      <div className="flex gap-6 text-sm text-gray-600">
                        <span>Employment: {(occupation.employment / 1000000).toFixed(2)}M</span>
                        <span>Median Wage: ${occupation.medianWage.toLocaleString()}</span>
                        {occupation.recentLayoffs > 0 && (
                          <span className="text-red-600 font-semibold">
                            Recent Layoffs: {occupation.recentLayoffs.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    {/* Analysis */}
                    <div className="mb-6 mt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Situation Analysis</h4>
                      <p className="text-gray-700">{occupation.analysis}</p>
                      {occupation.scoreChangeReason && (
                        <p className="text-sm text-gray-600 mt-2 italic">
                          <strong>This week:</strong> {occupation.scoreChangeReason}
                        </p>
                      )}
                    </div>

                    {/* Automation Breakdown */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Automation Risk Breakdown</h4>
                      <div className="space-y-2">
                        {Object.entries(occupation.automation).map(([type, value]) => (
                          <div key={type}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="capitalize text-gray-700">{type}</span>
                              <span className="font-semibold text-gray-900">{value}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{ width: `${value}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Protection Factors & Vulnerabilities */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-600" />
                          Protection Factors
                        </h4>
                        {occupation.protectionFactors.length > 0 ? (
                          <ul className="space-y-1">
                            {occupation.protectionFactors.map((factor, idx) => (
                              <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                <span className="text-green-600">•</span>
                                {factor}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500 italic">Data coming soon</p>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          Vulnerabilities
                        </h4>
                        {occupation.vulnerabilities.length > 0 ? (
                          <ul className="space-y-1">
                            {occupation.vulnerabilities.map((vuln, idx) => (
                              <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                <span className="text-orange-600">•</span>
                                {vuln}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500 italic">Data coming soon</p>
                        )}
                      </div>
                    </div>

                    {/* Pivot Paths */}
                    {occupation.pivotPaths.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Recommended Pivot Paths</h4>
                        <div className="grid gap-3">
                          {occupation.pivotPaths.map((path, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                              <div>
                                <p className="font-semibold text-gray-900">{path.role}</p>
                                <p className="text-sm text-gray-600">Timeline: {path.timeline}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                path.demand === 'high' ? 'bg-green-100 text-green-800' :
                                path.demand === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {path.demand} demand
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredAndSortedData.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No occupations match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OccupationRiskTracker;